import { useCallback, useContext, useMemo } from 'react';
import { Cell, Column as RTColumn, Row, UsePaginationInstanceProps, UseTableCellProps, UseTableColumnProps, UseTableInstanceProps, useTable, useFlexLayout, usePagination } from 'react-table';
import { format } from 'date-fns/fp';
import { DecodedValueMap, QueryParamConfig, SetQuery } from 'use-query-params';
import { SelectedContext } from '@containers/Group.Contacts/Context';
import { ContactItem } from '@containers/Group.Contacts/interfaces';
import { cx } from '@utils';
import { Pagination } from '$admin/Project.Aggregate/Pagination';
import { Table } from '$admin/components/Table';
import * as Column from './Column';
import styles from './style/Table.css';

type Props = {
  data: ContactItem[];
  pagination: {
    pageCount: number;
    totalCount: number;
  };
  setQuery: SetQuery<{
    index: QueryParamConfig<number, number>;
    name: QueryParamConfig<string, string>;
    size: QueryParamConfig<number, number>;
  }>;
  query: DecodedValueMap<{
    index: QueryParamConfig<number, number>;
    name: QueryParamConfig<string, string>;
    size: QueryParamConfig<number, number>;
  }>;
};

export const GroupContactsTable = ({ query, setQuery, ...props }: Props) => {
  const columns = useMemo<RTColumn<ContactItem>[]>(() => [
    {
      Cell: Column.Checkbox.Cell,
      Header: Column.Checkbox.Filter,
      id: 'checkbox',
      minWidth: 50,
      maxWidth: 50,
      width: 50,
    },
    {
      Cell: Column.Name.Cell,
      Header: `Name`,
      id: `name`,
      width: 80,
    },
    {
      Cell: Column.Email.Cell,
      Header: `Email`,
      id: `email`,
      width: 100,
    },
    {
      Cell: data => <div className={styles.trunc}>{data.row.original.title}</div>,
      Header: `Title`,
      accessor: `title`,
      id: `title`,
      width: 100,
    },
    {
      Header: `Company`,
      accessor: `company`,
      Cell: data => <div className={styles.trunc}>{data.row.original.company}</div>,
      id: `company`,
    },
    {
      Header: `Tags`,
      Cell: Column.Tags.Cell,
      id: `tags`,
      width: 125,
    },
  ], []);

  const params = {
    columns,
    data: props.data,
    initialState: {
      pageIndex: 0,
      pageSize: 25,
    },
    manualPagination: true,
    pageCount: props.pagination.pageCount,
  };

  const {
    getTableBodyProps,
    getTableProps,
    headerGroups,
    pageOptions,
    prepareRow,
    rows,
    setPageSize,
  } = useTable<ContactItem>(
    params,
    useFlexLayout,
    usePagination,
  );

  const nextPage = useCallback(() => {
    setQuery({ index: query.index + 1 }, 'pushIn');
  }, [
    query.index,
    setQuery,
  ]);

  const previousPage = useCallback(() => {
    const prevIndex = query.index - 1;
    const index = prevIndex > 0
        ? prevIndex
        : undefined;
    setQuery({ index }, 'pushIn');
  }, [
    query.index,
    setQuery,
  ]);

  const canPreviousPage = query.index > 0;
  const canNextPage = query.index + 1 < props.pagination.pageCount;

  const paginationProps: PaginationProps = {
    canNextPage,
    canPreviousPage,
    nextPage,
    pageCount: props.pagination.pageCount,
    pageIndex: query.index,
    pageSize: query.size,
    previousPage,
    pageOptions,
    setPageSize,
  };

  const [selected] = useContext(SelectedContext);

  return (
    <div
      {...getTableProps()}
      className={styles.root}>
      <div className={styles.wrap}>
        {headerGroups.map((headerGroup, i) => (
          <Table.Header
            key={i}
            {...headerGroup.getHeaderGroupProps({ className: styles.th })}>
            {headerGroup.headers.map((column, i) =>
              <div
                key={column.id}
                {...column.getHeaderProps(headerProps)}
                className={styles.header}>
                {column.render('Header')}
              </div>)}
          </Table.Header>))}
      </div>

      <div
        {...getTableBodyProps()}
        className={styles.body}>
        {rows.map((row: Row<ContactItem>) => {

          prepareRow(row);

          const classnames = cx(styles.row, {
            [styles.marked]: selected.ids.has(row.original.id),
          });

          return (
            <div
              key={row.original.id}
              {...row.getRowProps()}
              className={classnames}>
              {row.cells.map((cell: Cell<ContactItem>, i) =>
                <div
                  key={i}
                  {...cell.getCellProps(cellProps)}
                  className={styles.cell}>
                  {cell.render('Cell')}
                </div>)}
            </div>
          );
        })}
      </div>
      <div className={styles.pagination}>
        <Pagination {...paginationProps} />
      </div>
    </div>
  );
};

GroupContactsTable.displayName = 'GroupContacts.Table';

type PaginationProps = {
  pageSizeOptions?: number[];
} & Pick<UseTableInstanceProps<ContactItem>['state'],
    | 'pageIndex'
    | 'pageSize'>
  & Pick<UsePaginationInstanceProps<ContactItem>,
    | 'canNextPage'
    | 'canPreviousPage'
    | 'nextPage'
    | 'previousPage'
    | 'pageCount'
    | 'pageOptions'
    | 'setPageSize'>;

type CellFn = Parameters<UseTableCellProps<ContactItem>['getCellProps']>[number];
type HeaderFn = Parameters<UseTableColumnProps<ContactItem>['getHeaderProps']>[number];

const cellProps: CellFn = (props, { cell }) => {
  if (cell.column.id === 'checkbox') {
    return [props, {
      style: {
        flex: 1,
        alignItems: 'center',
      },
    }];
  }

  return getStyles(props, 'center');
};

const headerProps: HeaderFn = (props, { column }) => {
  if (column.id === 'checkbox') {
    return [props, {
      style: {
        flex: 1,
        alignItems: 'center',
      },
    }];
  }

  return getStyles(props, 'center');
};

const getStyles = (props, align = 'flex-start') => [
  props,
  {
    style: {
      justifyContent: 'flex-start',
      alignItems: align,
      display: 'flex',
    },
  },
];

const time = format('M/d/yy');